import { formatISO } from "date-fns";
import { z } from "zod";
import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import type { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import {
    QuarterMonth,
    RecurrenceLeadUnit,
    RelativeCalendarPeriod,
    RelativeCalendarPosition,
    RelativeDayType,
    WorkEndDateMode,
    WorkStartDateMode,
} from "@common/types/serviceTemplateRecurrence";
import { organizationMemberHasWorkspaceAccess } from "./applyCloseTemplateOrganizationMembers";
import { getLastCompletedMonthDates } from "./closePeriodSelector.utils";

// Fields only meaningful for the "schedule" wizard variant (recurring
// creation), nested so they never collide with the "apply" variant's
// same-named startDate/endDate (a one-off close period rather than a
// recurring active window).
export const scheduleDefaultsSchema = z.object({
    period: z.nativeEnum(ServiceTemplateRecurrencePeriod).nullable(),
    interval: z.number().int().positive(),
    startDate: z.string().date(),
    endDate: z.string().date().nullable(),
    maxOccurrenceCount: z.number().int().positive().nullable(),
    applicationLeadValue: z.number().int().nonnegative(),
    applicationLeadUnit: z.nativeEnum(RecurrenceLeadUnit),
    weekDays: z.array(z.number().int().min(0).max(6)),
    dateOfMonth: z.number().int().min(1).max(31).nullable(),
    datesOfMonth: z.array(z.number().int().min(1).max(31)),
    month: z.number().int().min(1).max(12).nullable(),
    quarterMonth: z.nativeEnum(QuarterMonth).nullable(),
    workStartDateMode: z.nativeEnum(WorkStartDateMode),
    workStartOffsetDays: z.number().int().nonnegative().nullable(),
    workStartSkipWeekend: z.boolean(),
    workStartWeekday: z.number().int().min(1).max(5).nullable(),
    workEndDateMode: z.nativeEnum(WorkEndDateMode),
    workEndOffsetDays: z.number().int().nonnegative().nullable(),
    workEndRelativePeriod: z.nativeEnum(RelativeCalendarPeriod).nullable(),
    workEndRelativePosition: z.nativeEnum(RelativeCalendarPosition).nullable(),
    workEndRelativeDayType: z.nativeEnum(RelativeDayType).nullable(),
    workEndRelativeDayOfPeriod: z.number().int().positive().nullable(),
});

export type ScheduleDefaults = z.infer<typeof scheduleDefaultsSchema>;

const applyCloseTemplateDefaultsSchema = z
    .object({
        startDate: z.string().date().nullable().optional(),
        endDate: z.string().date().nullable().optional(),
        workStartDate: z.string().date().nullable().optional(),
        workDeliveryDate: z.string().date().nullable().optional(),
        preparerId: z.number().int().nullable().optional(),
        reviewerId: z.number().int().nullable().optional(),
        managerId: z.number().int().nullable().optional(),
        // Only populated when the wizard variant is "schedule".
        schedule: scheduleDefaultsSchema.optional(),
    })
    .superRefine(
        ({ startDate, endDate, workStartDate, workDeliveryDate }, ctx) => {
            if (!startDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Start date is required",
                    path: ["startDate"],
                });
            }
            if (!endDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "End date is required",
                    path: ["endDate"],
                });
            }
            if (!workStartDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Work start date is required",
                    path: ["workStartDate"],
                });
            }
            if (!workDeliveryDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Work end date is required",
                    path: ["workDeliveryDate"],
                });
            }
            if (!startDate || !endDate) {
                return;
            }
            if (endDate < startDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "End date must be on or after start date",
                    path: ["endDate"],
                });
            }
            if (
                workStartDate &&
                workDeliveryDate &&
                workDeliveryDate < workStartDate
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        "Work end date must be on or after work start date",
                    path: ["workDeliveryDate"],
                });
            }
        },
    );

export const applyCloseTemplateEntityRowSchema = z
    .object({
        workspaceId: z.string().uuid(),
        // Only populated for the "schedule" wizard variant, which creates
        // recurring schedules keyed by entity uuid rather than numeric id.
        entityUuid: z.string().uuid().nullable().optional(),
        clientLabel: z.string(),
        entityName: z.string(),
        entityLogo: z.string().nullable().optional(),
        entityIconColor: z.string().optional().default(""),
        isSelected: z.boolean(),
        startDate: z.string().date().nullable().optional(),
        endDate: z.string().date().nullable().optional(),
        workStartDate: z.string().date().nullable().optional(),
        workDeliveryDate: z.string().date().nullable().optional(),
        preparerId: z.number().int().nullable().optional(),
        reviewerId: z.number().int().nullable().optional(),
        managerId: z.number().int().nullable().optional(),
        clientId: z.number().int().nullable().optional(),
    })
    .superRefine((row, ctx) => {
        if (!row.isSelected) {
            return;
        }
        if (!row.startDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Start date is required",
                path: ["startDate"],
            });
        }
        if (!row.endDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "End date is required",
                path: ["endDate"],
            });
        }
        if (!row.workStartDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Work start date is required",
                path: ["workStartDate"],
            });
        }
        if (!row.workDeliveryDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Work end date is required",
                path: ["workDeliveryDate"],
            });
        }
        if (row.startDate && row.endDate && row.endDate < row.startDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "End date must be on or after start date",
                path: ["endDate"],
            });
        }
        if (
            row.workStartDate &&
            row.workDeliveryDate &&
            row.workDeliveryDate < row.workStartDate
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Work end date must be on or after work start date",
                path: ["workDeliveryDate"],
            });
        }
    });

export type ApplyCloseTemplateEntityRow = z.infer<
    typeof applyCloseTemplateEntityRowSchema
>;

export const applyCloseTemplateWizardValuesSchema = z.object({
    defaults: applyCloseTemplateDefaultsSchema,
    entityRows: z.record(z.string(), applyCloseTemplateEntityRowSchema),
});

export type ApplyCloseTemplateWizardValues = z.infer<
    typeof applyCloseTemplateWizardValuesSchema
>;

const applyCloseTemplateStep1FormSchema = z.object({
    defaults: applyCloseTemplateDefaultsSchema,
});

const applyCloseTemplateStep2BaseSchema = z
    .object({
        entityRows: z.record(z.string(), applyCloseTemplateEntityRowSchema),
    })
    .refine(
        (data) => Object.values(data.entityRows).some((row) => row.isSelected),
        {
            message: "Select at least one entity",
            path: ["entityRows"],
        },
    );

/** Step 1: defaults preparer/reviewer/manager must belong to org workspaces shown in the wizard. */
export function createApplyCloseTemplateStep1FormSchema(
    membersWithOrgWorkspaceAccess: OrganizationMember[],
) {
    const eligibleUserIds = new Set(
        membersWithOrgWorkspaceAccess.map((m) => m.user.id),
    );
    return applyCloseTemplateStep1FormSchema.superRefine((data, ctx) => {
        for (const field of [
            "preparerId",
            "reviewerId",
            "managerId",
        ] as const) {
            const userId = data.defaults[field];
            if (userId == null) {
                continue;
            }
            if (!eligibleUserIds.has(userId)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        "Choose a team member who has access to at least one client workspace",
                    path: ["defaults", field],
                });
            }
        }
    });
}

const ROLE_FIELD_MAP: Array<{
    field: "preparerId" | "reviewerId" | "managerId";
    role: TaskTemplateAssigneeRole;
    label: string;
}> = [
    {
        field: "preparerId",
        role: TaskTemplateAssigneeRole.PREPARER,
        label: "Preparer",
    },
    {
        field: "reviewerId",
        role: TaskTemplateAssigneeRole.REVIEWER,
        label: "Reviewer",
    },
    {
        field: "managerId",
        role: TaskTemplateAssigneeRole.MANAGER,
        label: "Manager",
    },
];

interface EntityRowAssigneeFields {
    isSelected: boolean;
    workspaceId: string;
    preparerId?: number | null;
    reviewerId?: number | null;
    managerId?: number | null;
}

/** Requires assignees for template roles and checks each assignee can access the entity's workspace. */
export function refineSelectedEntityRowAssignees({
    entityRows,
    ctx,
    members,
    activeRoles,
}: {
    entityRows: Record<string, EntityRowAssigneeFields>;
    ctx: z.RefinementCtx;
    members: OrganizationMember[];
    activeRoles: Set<TaskTemplateAssigneeRole>;
}) {
    for (const [idStr, row] of Object.entries(entityRows)) {
        if (!row.isSelected) {
            continue;
        }
        for (const { field, role, label } of ROLE_FIELD_MAP) {
            const userId = row[field];
            if (userId == null) {
                if (activeRoles.has(role)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `${label} is required`,
                        path: ["entityRows", idStr, field],
                    });
                }
                continue;
            }
            const member = members.find((m) => m.user.id === userId);
            if (
                !member ||
                !organizationMemberHasWorkspaceAccess(member, row.workspaceId)
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        "Choose a team member who has access to this client workspace",
                    path: ["entityRows", idStr, field],
                });
            }
        }
    }
}

/** Validates entity rows, selection, and per-entity workspace access for assignees (step 2). */
export function createApplyCloseTemplateStep2ValidationSchema(
    members: OrganizationMember[],
    activeRoles: Set<TaskTemplateAssigneeRole>,
) {
    return applyCloseTemplateStep2BaseSchema.superRefine((data, ctx) => {
        refineSelectedEntityRowAssignees({
            entityRows: data.entityRows,
            ctx,
            members,
            activeRoles,
        });
    });
}

/** Builds the per-entity wizard rows shared by the apply and schedule variants; only the `clientId` default differs (via `defaultClientIdByWorkspaceId`). */
export function buildEntityRowsFromWorkspaces(
    workspaces: WorkspaceSchema[] | undefined,
    defaultClientIdByWorkspaceId?: Record<string, number | null>,
): ApplyCloseTemplateWizardValues["entityRows"] {
    const entityRows: ApplyCloseTemplateWizardValues["entityRows"] = {};
    workspaces?.forEach((ws) => {
        ws.entities?.forEach((e) => {
            entityRows[String(e.id)] = {
                workspaceId: e.workspaceId,
                entityUuid: e.uuid,
                clientLabel: ws.name,
                entityName: e.name ?? String(e.id),
                entityLogo: e.logo ?? null,
                entityIconColor: e.iconColor ?? "",
                isSelected: false,
                startDate: null,
                endDate: null,
                workStartDate: null,
                workDeliveryDate: null,
                preparerId: null,
                reviewerId: null,
                managerId: null,
                clientId: defaultClientIdByWorkspaceId?.[e.workspaceId] ?? null,
            };
        });
    });
    return entityRows;
}

export function createApplyCloseTemplateInitialValues(
    workspaces: WorkspaceSchema[] | undefined,
    defaultClientIdByWorkspaceId?: Record<string, number | null>,
): ApplyCloseTemplateWizardValues {
    const entityRows = buildEntityRowsFromWorkspaces(
        workspaces,
        defaultClientIdByWorkspaceId,
    );
    const { startDate, endDate } = getLastCompletedMonthDates();
    return {
        defaults: {
            startDate,
            endDate,
            workStartDate: formatISO(new Date(), { representation: "date" }),
            workDeliveryDate: null,
            preparerId: null,
            reviewerId: null,
            managerId: null,
        },
        entityRows,
    };
}
